import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { SessionStorageService } from '../../services/session-storage.service';
import { map, catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import {
  AnnuityItem,
  CalculateIncomeData,
  FaCarrier,
  FaProduct,
} from '../../models/annuity-models';
import { RightBridgeApiService } from '../../services/right-bridge-api.service';
import { EnvironmentService } from '../../services/environment.service';
import { GridResults, ApiResponse } from '../../models/general-models';
import { ErrorReportingService } from '../../services/error-reporting.service';
import { AdminService } from '../../admin/admin.service';
import { CannexResponse, GroupProfile } from '../../models/global.model';
@Injectable({
  providedIn: 'root',
})
export class AnnuityWizardService {
  private http = inject(HttpClient);
  private ss = inject(SessionStorageService);
  private rbs = inject(RightBridgeApiService);
  private envSvc = inject(EnvironmentService);
  private errSvc = inject(ErrorReportingService);
  private adminService = inject(AdminService);

  private environment;
  private baseUrl: string;
  private cannexBaseUrl: string;
  private firm: string;
  private app = 'aw';
  private org: string;

  constructor() {
    this.environment = this.envSvc.get();
    this.baseUrl = this.environment.apiBase;
    this.cannexBaseUrl = this.environment.cannexBase;
    this.org = this.environment.org;
    this.firm = this.environment.firm || '';
  }

  gridData(type: string): Observable<ApiResponse<GridResults>> {
    const id = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', 'aw')
      .set('X-RB-O', this.org);

    const url = `${this.baseUrl}/Annuity/${type}`;
    return this.http
      .get<ApiResponse<GridResults>>(url, {
        headers: reqHeaders,
        reportProgress: true,
      })
      .pipe(
        map(res => {
          return res;
        }),
        catchError((error: any) => {
          return throwError(() => error.error || 'Server error');
        })
      );
  }

  vaFundFees(
    ccode: string | number,
    cusip: string | number
  ): Observable<GridResults> {
    const sess = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', 'aw')
      .set('X-RB-O', this.org);
    const url = `${this.baseUrl}/Annuities/VAFunds?contractCode=${ccode}&cusip=${cusip}`;

    return this.http.get<GridResults>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return throwError(() => error.error || 'Server error');
      })
    );
  }

  vaFundFeesV2(annuityId: string | number): Observable<GridResults> {
    const sess = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', 'aw')
      .set('X-RB-O', this.org);
    const url = `${this.baseUrl}/Annuity/VAFunds?annuityId=${annuityId}`;

    return this.http.get<GridResults>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return throwError(() => error.error || 'Server error');
      })
    );
  }

  faFundFees(bridge: string): Observable<GridResults> {
    const sess = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', 'aw')
      .set('X-RB-O', this.org);
    const formattedBridge = bridge;
    if (bridge.includes('FA')) {
      formattedBridge.replace('FA', '');
    }
    const url = `${this.baseUrl}/Annuities/FAFunds?bridgeId=${formattedBridge}`;

    return this.http.get<GridResults>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return throwError(() => error.error || 'Server error');
      })
    );
  }

  faFundFeesV2(annuityId: string | number): Observable<GridResults> {
    const sess = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', 'aw')
      .set('X-RB-O', this.org);
    const url = `${this.baseUrl}/Annuity/FARates?annuityId=${annuityId}`;

    return this.http.get<GridResults>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError(error => {
        return throwError(() => error.error || 'Server error');
      })
    );
  }

  getFaCarrierGroups(): Observable<{ carriers: FaCarrier[] }> {
    const sess = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', 'aw')
      .set('X-RB-O', this.org);
    const url = `${this.baseUrl}/Annuities/FAProductCarriers`;

    return this.http
      .get<{ carriers: FaCarrier[] }>(url, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          return throwError(() => error.error || 'Server error');
        })
      );
  }

  getFaAnnuities(
    code: string | number
  ): Observable<{ FAProducts: FaProduct[] }> {
    const sess = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', 'aw')
      .set('X-RB-O', this.org);
    const url =
      this.baseUrl +
      '/Annuities/UnitFAProductsByCarrier?carrierGroupId=' +
      code;

    return this.http
      .get<{ FAProducts: FaProduct[] }>(url, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          return throwError(() => error.error || 'Server error');
        })
      );
  }

  getAnnuityFactSheet(cusip: string | number): Observable<Blob | null> {
    const firm = this.ss.get('firm');
    const payload = {
      ReportType: 'template',
      parameters: { AnnuityID: cusip },
      UnitID: firm,
      UIConfig: 'annuity-factsheet',
    };
    return this.rbs.getCustomReport(payload, 'aw', false, 'application/pdf');
  }

  patchAnnuities(
    profile: string,
    payload: {
      AWProcessControl: {
        DeselectProduct: boolean;
        SelectProduct: boolean;
        Cusip: string | number;
        LBRider: string | number;
        GMABRider: string | number;
        AnnuityId: string | number;
        ContractCode: string | number;
        ProductType: string | number;
        RERRider: string | number;
        SWRRider: string | number;
        STDDBRider: string | number;
        EnhancedDBRider: string | number;
        Term: string | number;
      };
    }
  ): Observable<GroupProfile> {
    return this.rbs.patchGroupProfile(payload, 'AWResults', profile, 'aw');
  }

  processAnnuities(
    profile: string,
    omitSaveEmpty: boolean = false
  ): Observable<GroupProfile> {
    return this.rbs.getGroupProfile(
      profile,
      'AWResults',
      'aw',
      false,
      false,
      omitSaveEmpty
    );
  }

  getReport(
    profile: string,
    details?: string,
    silent = false,
    vaDetails = false,
    validate = false,
    excludeAlternativeReasons = true
  ): Observable<Blob | null> {
    const id = this.ss.get('session');
    this.firm =
      this.firm && this.firm.length ? this.firm : this.rbs.setFirmId();
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', 'aw')
      .set('X-RB-O', this.org)
      .set('Accept', 'application/pdf');

    let url = `${this.baseUrl}/reports/aw/${profile}?selectedBridges=${details}&excludeAlternatives=${excludeAlternativeReasons}`;

    if (vaDetails) {
      url += '&includeVAInvestmentOptions=true';
    }

    if (silent) {
      url += '&returnReport=false';
    }

    if (validate) {
      url += '&isValidation=true';
    }

    return this.http
      .post(url, null, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError(error => {
          return throwError(() => error.error || 'Server error');
        })
      );
  }

  getNoSaveReport(
    profileData: string,
    details?: string,
    vaDetails = false,
    validate = false,
    excludeAlternativeReasons = true
  ): Observable<Blob | null> {
    const id = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', 'aw')
      .set('X-RB-O', this.org)
      .set('Accept', 'application/pdf');
    let url = `${this.baseUrl}/reports/aw?selectedBridges=${details}&excludeAlternatives=${excludeAlternativeReasons}`;

    if (vaDetails) {
      url += '&includeVAInvestmentOptions=true';
    }

    if (validate) {
      url += '&isValidation=true';
    }

    return this.http
      .post(url, profileData, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError(error => {
          return throwError(() => error.error || 'Server error');
        })
      );
  }

  getViaRates() {
    return this.rbs.getRequest<any>(
      `${this.rbs.baseUrl}/GridData/VIA`,
      null,
      'aw'
    );
  }

  getProductGrid(): Observable<ApiResponse<GridResults<AnnuityItem>>> {
    return this.rbs.getRequest<ApiResponse<GridResults<AnnuityItem>>>(
      `${this.rbs.baseUrl}/Annuity/ProductGrid`,
      null,
      'aw'
    );
  }

  getIncomeRiderGrid(
    data?: CalculateIncomeData
  ): Observable<ApiResponse<GridResults<AnnuityItem>>> {
    const payload = data ? data : {};

    return this.rbs.postRequest<any>(
      `${this.rbs.baseUrl}/Annuity/RiderGrid`,
      payload,
      null,
      null,
      'aw'
    );
  }

  getDeathRiderGrid(): Observable<ApiResponse<GridResults<AnnuityItem>>> {
    return this.rbs.getRequest<ApiResponse<GridResults<AnnuityItem>>>(
      `${this.rbs.baseUrl}/Annuity/DeathRiderGrid`,
      null,
      'aw'
    );
  }

  getGroupProfile(group: string) {
    return this.rbs.postGroupProfile(group, 'aw', {});
  }

  processAnnuitiesGroupProfile(profileData: any): Observable<GroupProfile> {
    return this.rbs.postGroupProfile('AWResults', 'aw', profileData);
  }

  getIncomeIterations(profileId: string): Observable<AnnuityItem> {
    return this.rbs.getRequest<AnnuityItem>(
      `${this.rbs.baseUrl}/Annuities/IncomeIterations/${profileId}`,
      null,
      'aw'
    );
  }

  getCannexCalc(token: string, data: any): Observable<CannexResponse> {
    const reqHeaders = new HttpHeaders()
      .set('Accept', 'text/plain')
      .set('Content-Type', 'application/json')
      .set('Authorization', `Bearer ${token}`);
    const url = `${this.cannexBaseUrl}/Cannex`;

    return this.http
      .post<CannexResponse>(url, data, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          return throwError(() => error.error || 'Server error');
        })
      );
  }
}
